/**
 * De veldtypes van de gegevenstabel bij huishoudens en personas.
 *
 * Het intakeformulier kent er negen; hier staan er zeven. Notitie en matrix
 * vallen af omdat ze in de gegevenstabel niet bestaan als losse regel: een notitie
 * landt al als aparte notitie op het huishouden, en een matrixantwoord wordt bij
 * het opslaan al opgesplitst in één regel per cel ('rij / kolom').
 *
 * Het type wordt opgeslagen als `FieldType` en niet als `Type`. `Type` is op
 * `PersonaDocuments` al bezet door de documentsoort (Zorgovereenkomst, Contract,
 * ID, AVG — 141 regels met een eigen keuzelijst in het scherm), en op
 * `PersonaData` door de markering `privacy-consent`.
 *
 * AI-CONTEXT: See docs/features/gegevensveld-types.md
 *
 * @typedef {object} DataFieldType
 * @property {string} value - Zoals opgeslagen in FieldType
 * @property {string} label - Nederlandse naam
 */

/**
 * DE BRON: alles wat je op een intakeformulier kunt vragen, in de volgorde
 * waarin de bouwer ze aanbiedt. Stond eerder als losse `<option>`s in
 * IntakeFormBuilder.jsx, naast deze lijst — twee plekken die uiteen gaan lopen
 * zodra er één type bijkomt.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * @type {Array<DataFieldType>}
 */
const INTAKE_FORM_FIELD_TYPES = [
    { value: 'text', label: 'Tekst' },
    { value: 'number', label: 'Getal' },
    { value: 'date', label: 'Datum' },
    { value: 'notitie', label: 'Notitie' },
    { value: 'multiple-one', label: 'Meerkeuze - één antwoord' },
    { value: 'multiple-many', label: 'Meerkeuze - meerdere antwoorden' },
    { value: 'upload', label: 'Document uploaden' },
    { value: 'checkbox', label: 'Vinkje (ja/nee)' },
    { value: 'matrix', label: 'Matrix (tabel)' },
]

/** Types die in de gegevenstabel niet bestaan als losse regel — zie de kop. */
const NIET_IN_GEGEVENSTABEL = ['notitie', 'matrix']

/**
 * Afgeleid en niet apart opgeschreven, zodat een nieuw veldtype vanzelf op
 * beide plekken dezelfde naam draagt.
 *
 * @type {Array<DataFieldType>}
 */
const DATA_FIELD_TYPES = INTAKE_FORM_FIELD_TYPES.filter(
    (t) => !NIET_IN_GEGEVENSTABEL.includes(t.value),
)

// Notitie en matrix zijn in de gegevenstabel niet te kiezen, maar kunnen wél op
// een regel staan die uit een intakeformulier komt. Zonder die twee leest zo'n
// regel als de kale technische naam.
const ALLE_LABELS = INTAKE_FORM_FIELD_TYPES.reduce(
    (acc, t) => ({ ...acc, [t.value]: t.label }), {},
)

/**
 * Nederlandse naam van een veldtype.
 *
 * Een leeg type is de normale toestand: de gegevens die vóór deze wijziging
 * handmatig zijn ingevoerd hebben er geen.
 *
 * @param {string} [value] - Zoals opgeslagen in FieldType
 * @returns {string}
 */
const dataFieldTypeLabel = (value) => {
    if (!value) return '—'
    return ALLE_LABELS[value] || value
}

/**
 * Bepaalt hoe één gegevensregel ingevuld hoort te worden.
 *
 * `FieldType` wint altijd. Staat dat er niet, dan gelden de twee uitzonderingen
 * die er al waren en op de TITEL staan — die zijn ouder dan `FieldType` en
 * dekken de ruim tienduizend regels zonder type.
 *
 * @param {object} [item] - Een regel uit PersonaData
 * @returns {string} een waarde uit DATA_FIELD_TYPES, of 'housing' / 'gender'
 */
const resolveDataFieldType = (item) => {
    if (item?.FieldType) return item.FieldType

    if (item?.Title === 'Type woning') return 'housing'
    if (item?.Title === 'Geslacht') return 'gender'

    // Een datumtitel krijgt een datumkiezer, maar alleen als de waarde leeg is
    // of al een datum: een oude vrije tekst ('30 april 2034') zou in een
    // datumkiezer onzichtbaar worden. Die blijft tekst tot hij is omgezet
    // (functions/scripts/backfillPersonaDateValues.js).
    if (isDateTitle(item?.Title) && (!item?.Value || isIsoDate(item.Value))) return 'date'

    // Een opgeslagen link is in de praktijk een geüpload document, ook als er
    // nooit een type op gezet is.
    if (String(item?.Value || '').startsWith('https://')) return 'upload'

    return 'text'
}

/**
 * Titels uit de keuzelijst van de gegevenstabel die een datum dragen. De
 * nachtelijke controle (functions/personas/checkPersonaDateAlerts.js) gaat af
 * op de eerste twee. Spiegel van DATE_TITLES in
 * functions/scripts/backfillPersonaDateValues.js.
 *
 * @type {Array<string>}
 */
const DATE_TITLES = ['Geboortedatum', 'Document verloopdatum', 'Kaart geldig tot']

/**
 * @param {string} [title]
 * @returns {boolean}
 */
const isDateTitle = (title) => DATE_TITLES.includes(title)

/**
 * @param {*} value
 * @returns {boolean} of dit de opslagvorm van een datum is (`YYYY-MM-DD`)
 */
const isIsoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)

/**
 * @param {string} [type]
 * @returns {boolean} of dit type antwoordopties nodig heeft
 */
const isMultipleType = (type) => type === 'multiple-one' || type === 'multiple-many'

export { DATA_FIELD_TYPES, INTAKE_FORM_FIELD_TYPES, DATE_TITLES, dataFieldTypeLabel, resolveDataFieldType, isMultipleType, isDateTitle, isIsoDate }
export default DATA_FIELD_TYPES
